import { AfterViewInit, ChangeDetectionStrategy, Component, computed, ElementRef, input, InputSignal, OnDestroy, output, viewChild } from '@angular/core';
import { GoogleMap } from '@angular/google-maps';
import { LocationsSkeletonService } from '../../../locations-skeleton.service';
import { NgClass } from '@angular/common';
import { MatButton, MatIconButton } from '@angular/material/button';
import { GxInputDirective } from '../../../../../directives/gxinput.directive';
import { MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatPrefix, MatSuffix } from '@angular/material/form-field';
import { Subject } from 'rxjs';
import LatLngLiteral = google.maps.LatLngLiteral;

export interface PlaceAutocompleteMarkerEvent {
  position: LatLngLiteral,
  minimized: boolean
}

@Component({
  selector: 'app-map-ui',
  templateUrl: './map-ui.component.html',
  styleUrls: ['./map-ui.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    MatFormField,
    MatIcon,
    MatPrefix,
    MatInput,
    GxInputDirective,
    MatIconButton,
    MatSuffix,
    MatButton,
    NgClass,
  ],
})
export class MapUiComponent implements AfterViewInit, OnDestroy {
  public googleMap: InputSignal<GoogleMap> = input.required();
  public cvButtonActive: InputSignal<boolean> = input.required();
  public markersMinimized: InputSignal<boolean> = input.required();
  public rightPanelWidth: InputSignal<number> = input.required();

  protected readonly containerWidthPx = computed(() => {
    return this.skeletonService.calculateRightPanelWidth(this.rightPanelWidth())
        - 16 // container is positioned 8px from left, leave another 8px from the right
        - 10 // drag handle width
        - 16; // right padding
  });

  protected readonly placeAutocompleteMarker = output<PlaceAutocompleteMarkerEvent>();
  protected readonly toggleMinimizeMarkers = output<void>();
  protected readonly toggleCvMode = output<void>();

  protected readonly pacInput = viewChild<ElementRef>('pacInput');

  protected inputListenerId: google.maps.MapsEventListener;
  protected searchBox: google.maps.places.SearchBox;

  protected readonly componentDestroyed: Subject<void> = new Subject<void>();

  constructor(protected skeletonService: LocationsSkeletonService) {
  }

  ngAfterViewInit() {
    this.initAutocomplete();
  }

  ngOnDestroy() {
    this.removeInputListener();
    this.componentDestroyed.next();
  }

  onToggleMinimizeMarkers() {
    this.toggleMinimizeMarkers.emit();
  }

  onToggleCvMode() {
    this.toggleCvMode.emit();
  }

  /**
   * Initiate location search box
   */
  initAutocomplete(): boolean {
    // Create the search box and link it to the UI element.
    if (!this.pacInput) {
      console.warn('pacInput is undefined');
      return false;
    }

    // will create the search box multiple times on language change (creates darker shadow around
    // dropdown but only triggers events once)
    this.searchBox = new google.maps.places.SearchBox(this.pacInput().nativeElement);

    // Listen for the event fired when the user selects a prediction and retrieve
    // more details for that place.
    this.inputListenerId = this.searchBox.addListener('places_changed', () => {
      const places = this.searchBox.getPlaces();

      if (!places.length) {
        return;
      }

      // For each place, get the location
      const bounds = new google.maps.LatLngBounds();

      places.forEach((place) => {
        if (!place.geometry?.location) {
          console.warn('Returned place contains no geometry');
          return;
        }

        this.placeAutocompleteMarker.emit({
          position: place.geometry.location.toJSON(),
          minimized: this.markersMinimized(),
        });

        if (place.geometry.viewport) {
          // Only geocodes have viewport.
          bounds.union(place.geometry.viewport);
        } else {
          bounds.extend(place.geometry.location);
        }
      });

      this.googleMap().googleMap.fitBounds(bounds);
    });
  }

  /**
   * Clear autocomplete marker when user press 'clear' button
   */
  clearAutocomplete() {
    this.pacInput().nativeElement.value = '';
    this.placeAutocompleteMarker.emit(null);
  }

  /**
   * Clear autocomplete marker when user deletes input value (backspace, del...)
   */
  onAutocompleteChange() {
    setTimeout(() => {
      if (!this.pacInput().nativeElement.value) {
        this.placeAutocompleteMarker.emit(null);
      }
    });
  }

  private removeInputListener() {
    if (this.inputListenerId) {
      google.maps.event.removeListener(this.inputListenerId);
    }
  }


}
